'use client';

import { useState, useEffect, Suspense } from 'react';
import { Search, Bell, Home, Users, MapPin, Building2, DoorOpen, ClipboardCheck, Wrench, BarChart3, Settings, WifiOff } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { isDemoMode, getCustomerFilter, getDeliveryLocationFilter, getDoorFilter } from '@/lib/demo-mode';
import { DemoModeToggle, DemoModeIndicator } from '@/components/ui/DemoModeToggle';
import NewDamageReports from '@/components/dashboard/NewDamageReports';
import { useAuth } from '@/lib/auth/auth-context';

interface DashboardStats {
  totalCustomers: number;
  totalLocations: number;
  totalDoors: number;
  protocolCount: number;
  openRepairs: number;
}

export default function Dashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 1,
    totalLocations: 1,
    totalDoors: 25,
    protocolCount: 2,
    openRepairs: 4
  });

  const [demoMode, setDemoMode] = useState(false);
  const [isOffline, setIsOffline] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 🔐 ROLLENBASIERTE ERP-BOXEN-STEUERUNG
  const { user } = useAuth();
  const shouldShowReminders = user && (user.role === 'verwaltung' || user.role === 'admin');

  useEffect(() => {
    setDemoMode(isDemoMode());
  }, []);

  useEffect(() => {
    // Offline-Erkennung
    const updateOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    updateOnlineStatus();

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setIsLoading(true);
      
      try {
        // 🎯 1. KUNDEN: Alle Live- oder Demo-Kunden laden
        const customerFilter = getCustomerFilter(demoMode);
        const { data: customers } = await supabase
          .from("customers")
          .select("id")
          .match(customerFilter)
          .eq("status", true);

        const customerIds = customers?.map((c: any) => c.id) || [];

        // 🎯 2. LIEFERORTE: Alle Lieferorte von allen relevanten Kunden
        let locationQuery = supabase
          .from("delivery_locations")
          .select("id, customer_id")
          .eq("status", true);

        if (customerIds.length > 0) {
          locationQuery = locationQuery.in("customer_id", customerIds);
        }

        const { data: locations } = await locationQuery;
        const locationIds = locations?.map((l: any) => l.id) || [];

        // 🎯 3. TÜREN: Alle Türen von allen relevanten Lieferorten
        // 🎯 3. TÜREN LADEN (über Sites-Relation für korrekte Zuordnung)
        let doorQuery = supabase
          .from("doors")
          .select(`
            id,
            next_inspection_date,
            status,
            site_id,
            sites!inner(
              id,
              delivery_location_id
            )
          `);

        if (locationIds.length > 0) {
          doorQuery = doorQuery.in("sites.delivery_location_id", locationIds);
        }

        const { data: doors } = await doorQuery;

        // 🎯 4. WARTUNGEN: Anzahl der abgeschlossenen Einzeltür-Wartungen (Bug #14 Fix)
        const { data: inspections } = await supabase
          .from("door_inspections")
          .select("id")
          .eq("status", "completed");

        const protocolCount = inspections?.length || 0;

        // 🎯 5. REPARATUREN: Alle offenen Reparaturen von allen relevanten Lieferorten
        let repairQuery = supabase
          .from("repairs")
          .select("id, status, delivery_location_id");

        if (locationIds.length > 0) {
          repairQuery = repairQuery.in("delivery_location_id", locationIds);
        }

        const { data: repairs } = await repairQuery;

        // Robuste Reparaturen-Zählung (Definition später verfeinern)
        const openRepairStatuses = ["offen", "open", "pending", "in_progress", "schadensmeldung"];
        const openRepairsCount = repairs?.filter((repair: any) => 
          openRepairStatuses.includes(repair.status?.toLowerCase())
        ).length || 0;

        // 🎯 6. STATS SETZEN
        setStats({
          totalCustomers: customers?.length || 0,
          totalLocations: locations?.length || 0,
          totalDoors: doors?.length || 0,
          protocolCount,
          openRepairs: openRepairsCount
        });

        setIsLoading(false);

      } catch (error) {
        console.error("Fehler beim Laden der Dashboard-Statistiken:", error);
        
        // 🔧 OFFLINE-OPTIMIERTE FALLBACK-DATEN
        setStats({
          totalCustomers: demoMode ? 3 : 3,
          totalLocations: demoMode ? 4 : 2,
          totalDoors: demoMode ? 7 : 26,
          protocolCount: demoMode ? 2 : 2,
          openRepairs: demoMode ? 2 : 8
        });
        
        setIsLoading(false);
        setIsOffline(true);
      }
    };

    fetchDashboardStats();
  }, [demoMode]);

  return (
    <div className="min-h-screen bg-slate-50">
      <DemoModeToggle />
      <DemoModeIndicator />
      
      {/* Offline-Banner */}
      {isOffline && (
        <div className="bg-yellow-500 text-white px-4 py-2 text-center text-sm">
          <div className="flex items-center justify-center space-x-2">
            <WifiOff className="h-4 w-4" />
            <span>Offline-Modus aktiv - Zeige gespeicherte Daten</span>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">InspecDoor©</h1>
              <p className="text-slate-300 mt-1">
                {demoMode 
                  ? 'Türprüfungs-Management für Metallbau Steffens - Demo-Version' 
                  : 'Türprüfungs-Management für Metallbau Steffens'
                }
                {isOffline && ' (Offline)'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Dashboard</h2>
          
          {/* 🎯 DASHBOARD-STATISTIKEN - FUNKTIONIERENDES PATTERN */}
          <style jsx>{`
            @media (pointer: coarse) {
              .touch-target {
                -webkit-tap-highlight-color: transparent;
                touch-action: manipulation;
              }
            }
          `}</style>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            <a href={`/kunden${demoMode ? '?demo=true' : ''}`} className="bg-blue-50 border border-blue-200 rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
              <div className="text-2xl font-bold text-blue-600">{stats.totalCustomers}</div>
              <div className="text-sm text-slate-600">Kunden</div>
              <div className="text-sm font-semibold text-blue-600 mt-2 flex items-center gap-1">Öffnen <span className="text-lg">→</span></div>
            </a>
            
            <a href={`/lieferorte${demoMode ? '?demo=true' : ''}`} className="bg-green-50 border border-green-200 rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
              <div className="text-2xl font-bold text-green-600">{stats.totalLocations}</div>
              <div className="text-sm text-slate-600">Lieferorte</div>
              <div className="text-sm font-semibold text-green-600 mt-2 flex items-center gap-1">Öffnen <span className="text-lg">→</span></div>
            </a>
            
            <a href={`/tueren${demoMode ? '?demo=true' : ''}`} className="bg-purple-50 border border-purple-200 rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
              <div className="text-2xl font-bold text-purple-600">{stats.totalDoors}</div>
              <div className="text-sm text-slate-600">Türen</div>
              <div className="text-sm font-semibold text-purple-600 mt-2 flex items-center gap-1">Öffnen <span className="text-lg">→</span></div>
            </a>
            
            <a href={`/pruefungen${demoMode ? '?demo=true' : ''}`} className="bg-yellow-50 border border-yellow-200 rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
              <div className="text-2xl font-bold text-yellow-600">{stats.protocolCount}</div>
              <div className="text-sm text-slate-600">Wartungen</div>
              <div className="text-sm font-semibold text-yellow-700 mt-2 flex items-center gap-1">Öffnen <span className="text-lg">→</span></div>
            </a>
            
            <a href={`/reparatur-management${demoMode ? '?demo=true' : ''}`} className="bg-red-50 border border-red-200 rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
              <div className="text-2xl font-bold text-red-600">{stats.openRepairs}</div>
              <div className="text-sm text-slate-600">Offene Reparaturen</div>
              <div className="text-sm font-semibold text-red-600 mt-2 flex items-center gap-1">Öffnen <span className="text-lg">→</span></div>
            </a>
          </div>

          {/* 🔔 NEUE SCHADENSMELDUNGEN - NUR FÜR VERWALTUNG UND ADMIN */}
          {!isOffline && shouldShowReminders && (
            <Suspense fallback={<div className="text-center py-4">Lade Schadensmeldungen...</div>}>
              <NewDamageReports demoMode={demoMode} />
            </Suspense>
          )}
          
          {/* Offline-Info */}
          {isOffline && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-900 mb-2">📱 Offline-Modus</h3>
              <p className="text-blue-800 text-sm">
                Alle wesentlichen Funktionen stehen offline zur Verfügung. Synchronisation erfolgt automatisch, sobald eine Internetverbindung besteht.
              </p>
            </div>
          )}
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <a href={`/kunden${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Kunden verwalten</h3>
            <p className="text-slate-600 text-sm">Kundenstammdaten und Kontakte pflegen</p>
            <div className="mt-4 text-blue-600 text-base font-bold flex items-center gap-1">Kunden-Übersicht <span className="text-lg">→</span></div>
          </a>

          <a href={`/lieferorte${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Lieferorte</h3>
            <p className="text-slate-600 text-sm">Standorte und Gebäude verwalten</p>
            <div className="mt-4 text-green-600 text-sm font-medium">→ Lieferorte-Übersicht</div>
          </a>

          <a href={`/standorte${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Standorte</h3>
            <p className="text-slate-600 text-sm">4-Level Hierarchie Navigation</p>
            <div className="mt-4 text-purple-600 text-sm font-medium">→ Standorte-Übersicht</div>
          </a>

          <a href={`/tueren${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Türen</h3>
            <p className="text-slate-600 text-sm">150+ Parameter und Prüfdokumentation</p>
            <div className="mt-4 text-purple-600 text-sm font-medium">→ Türen-Übersicht</div>
          </a>

          <a href={`/pruefungen${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Wartungen</h3>
            <p className="text-slate-600 text-sm">Prüfprotokolle erstellen und verwalten</p>
            <div className="mt-4 text-yellow-600 text-sm font-medium">→ Wartungen-Übersicht</div>
          </a>

          <a href={`/reparatur-management${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Reparatur-Management</h3>
            <p className="text-slate-600 text-sm">4-Stufen-Workflow und Kostentracking</p>
            <div className="mt-4 text-red-600 text-sm font-medium">→ Reparatur-Management</div>
          </a>

          <a href={`/auswertungen${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Auswertungen</h3>
            <p className="text-slate-600 text-sm">Prüfmonat-Übersicht und Zeiterfassung</p>
            <div className="mt-4 text-teal-600 text-sm font-medium">→ Auswertungen öffnen</div>
          </a>

          <a href={`/berichte${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Berichte</h3>
            <p className="text-slate-600 text-sm">Statistiken und Reports</p>
            <div className="mt-4 text-slate-600 text-sm font-medium">→ Berichte-Übersicht</div>
          </a>

          <a href={`/auswertungen/zeiterfassung${demoMode ? '?demo=true' : ''}`} className="bg-white rounded-lg shadow p-6 hover:shadow-xl hover:scale-105 transition-all cursor-pointer">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Zeiterfassung</h3>
            <p className="text-slate-600 text-sm">Geselle/Lehrling Auswertung</p>
            <div className="mt-4 text-blue-600 text-sm font-medium">→ Zeitauswertung</div>
          </a>
        </div>

        {/* PWA Info */}
        <div className="mt-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
          <h3 className="text-xl font-semibold mb-2">📱 Progressive Web App</h3>
          <p className="text-blue-100 mb-4">
            {demoMode 
              ? 'Demo-Version: Installieren Sie InspecDoor auf Ihrem Gerät für Offline-Zugriff auf Präsentationsdaten.'
              : 'Installieren Sie InspecDoor auf Ihrem Gerät für Offline-Zugriff und Push-Benachrichtigungen.'
            }
          </p>
          <div className="flex flex-wrap gap-4 text-sm">
            <span className={isOffline ? "text-green-300 font-semibold" : ""}>✓ Offline-Funktionalität</span>
            <span>✓ Push-Benachrichtigungen</span>
            <span>✓ App-Installation</span>
            <span>✓ Synchronisation</span>
          </div>
        </div>
      </div>
    </div>
  );
}
